import { supabase } from "../lib/supabase"

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL!
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY!

function getLocalDateString(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

async function getAuthContext(): Promise<{ userId: string; token: string } | null> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return null
  return { userId: session.user.id, token: session.access_token }
}

export interface SessionData {
  session_date?: string
  language?: string
  todays_load?: string[]
  important_items?: string[]
  waiting_items?: string[]
  focus_decision?: string
  other_decisions?: string[]
  obstacles?: string[]
  action_plan?: string
  action_time?: string
  ai_obstacle_response?: string
  ai_action_response?: string
  outcome?: "done" | "not_done" | "gave_up" | "postponed"
  outcome_note?: string
}

export async function createSession(data: SessionData): Promise<string | null> {
  try {
    const auth = await getAuthContext()
    if (!auth) {
      console.error("createSession: no auth session")
      return null
    }

    const payload = {
      user_id: auth.userId,
      session_date: data.session_date || getLocalDateString(),
      language: data.language,
      todays_load: data.todays_load,
      important_items: data.important_items,
      waiting_items: data.waiting_items,
      focus_decision: data.focus_decision,
      other_decisions: data.other_decisions,
      obstacles: data.obstacles,
      action_plan: data.action_plan,
      action_time: data.action_time,
      ai_obstacle_response: data.ai_obstacle_response,
      ai_action_response: data.ai_action_response,
      one_decision: data.focus_decision,
      decision_statement: data.action_plan,
      commit_time: data.action_time,
      obstacle_analysis: data.ai_obstacle_response,
      decision_support: data.ai_action_response,
    }

    const response = await fetch(`${SUPABASE_URL}/rest/v1/sessions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": SUPABASE_ANON_KEY,
        "Authorization": `Bearer ${auth.token}`,
        "Prefer": "return=representation"
      },
      body: JSON.stringify(payload)
    })

    if (!response.ok) {
      console.error("Session create failed:", await response.text())
      return null
    }

    const result = await response.json()
    return result[0]?.id || null
  } catch (err) {
    console.error("Session create error:", err)
    return null
  }
}

export async function updateSession(id: string, data: Partial<SessionData>): Promise<boolean> {
  try {
    const auth = await getAuthContext()
    if (!auth) return false

    const response = await fetch(`${SUPABASE_URL}/rest/v1/sessions?id=eq.${id}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "apikey": SUPABASE_ANON_KEY,
        "Authorization": `Bearer ${auth.token}`,
      },
      body: JSON.stringify({
        ...data,
        updated_at: new Date().toISOString()
      })
    })
    return response.ok
  } catch (err) {
    console.error("Session update error:", err)
    return false
  }
}

export async function getSessions(days: number = 90): Promise<any[]> {
  try {
    const auth = await getAuthContext()
    if (!auth) return []

    const since = new Date()
    since.setDate(since.getDate() - days)
    const sinceStr = since.toISOString().split('T')[0]

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/sessions?user_id=eq.${auth.userId}&session_date=gte.${sinceStr}&order=session_date.desc`,
      {
        headers: {
          "apikey": SUPABASE_ANON_KEY,
          "Authorization": `Bearer ${auth.token}`,
        }
      }
    )

    if (!response.ok) return []
    const data = await response.json()

    return data.map((s: any) => ({
      ...s,
      focus_decision: s.focus_decision || s.one_decision,
      action_plan: s.action_plan || s.decision_statement,
      action_time: s.action_time || s.commit_time,
    }))
  } catch (err) {
    console.error("Session fetch error:", err)
    return []
  }
}

export async function deleteSession(sessionId: string): Promise<boolean> {
  try {
    const auth = await getAuthContext()
    if (!auth) return false

    // Prefer: return=representation → silinen satırı döndürür.
    // Boş array gelirse RLS bloklamış, gerçek silme olmamış demektir.
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/sessions?id=eq.${sessionId}`,
      {
        method: "DELETE",
        headers: {
          "apikey": SUPABASE_ANON_KEY,
          "Authorization": `Bearer ${auth.token}`,
          "Prefer": "return=representation",
        }
      }
    )

    if (!response.ok) {
      console.error("Session delete failed:", response.status, await response.text())
      return false
    }

    const deleted = await response.json()
    if (deleted.length === 0) {
      console.error("Session delete: RLS engeli veya kayıt bulunamadı. Supabase'de DELETE politikası ekleyin.")
      return false
    }

    return true
  } catch (err) {
    console.error("Session delete error:", err)
    return false
  }
}
